import { useState } from 'react';
import { Check, ArrowLeft, ArrowRight, ShieldAlert, Zap, Crown, Construction } from 'lucide-react';
import { motion } from 'framer-motion';
import { Logo } from './Logo';
import { Footer } from './Footer';
import { GlowingEffect } from './GlowingEffect';

interface PricingProps {
  onBack: () => void;
  onSelectPlan?: () => void;
}

export const Pricing = ({ onBack, onSelectPlan }: PricingProps) => {
  const [isAnnual, setIsAnnual] = useState(false);
  
  const calculatePrice = (monthly: number) => {
    return isAnnual ? Math.floor(monthly * 0.8) : monthly;
  };

  const plans = [
    {
      name: "Starter",
      monthlyPrice: 499,
      description: "Ideal for individual creators launching their first products.",
      icon: <Zap className="size-5 text-emerald-400" />,
      features: [
        "1 brand account",
        "Secure 2 products",
        "Custom Login Page",
        "Track user access",
        "Daily leak scans",
        "Basic takedown support",
        "Owner dashboard",
        "Email support"
      ],
      limits: "Up to 1,000 members",
      cta: "Start Protecting",
      highlighted: false
    },
    {
      name: "Professional",
      monthlyPrice: 999,
      description: "Designed for scaling communities and high volume sales.",
      icon: <ShieldAlert className="size-5 text-blue-400" />,
      features: [
        "Everything in Starter",
        "Secure 5 products",
        "Advanced leak tracking",
        "Device-based security",
        "IP monitoring",
        "Fast legal takedowns",
        "Detailed security reports",
        "Priority support"
      ],
      limits: "Up to 5,000 members",
      cta: "Go Pro",
      highlighted: true
    },
    {
      name: "Agency",
      monthlyPrice: 1999,
      description: "Full protection for large teams and enterprise creators.",
      icon: <Crown className="size-5 text-amber-400" />,
      features: [
        "Everything in Professional",
        "Unlimited products",
        "Full white-label portal",
        "Custom domain support",
        "Advanced security rules",
        "24/7 dedicated support",
        "Custom account manager",
        "Full analytics suite"
      ],
      limits: "Up to 20,000 members",
      cta: "Elite Access",
      highlighted: false
    }
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="bg-background min-h-screen flex flex-col"
    >
      {/* Header */}
      <header className="w-full max-w-7xl mx-auto flex items-center justify-between py-10 px-6 z-30">
        <Logo />
        <button 
          onClick={onBack}
          className="group flex items-center gap-2 text-[10px] font-bold tracking-[0.3em] uppercase text-muted-foreground hover:text-foreground transition-all"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
          Back to Home
        </button>
      </header>

      <div className="flex-1 max-w-7xl mx-auto px-6 py-12 w-full">
        <div className="text-center max-w-4xl mx-auto mb-12 space-y-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="inline-flex items-center gap-3 px-3 py-1 rounded-full border border-border/50 bg-muted/20"
          >
            <span className="relative flex size-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex size-1.5 rounded-full bg-emerald-500" />
            </span>
            <span className="text-[9px] font-bold tracking-[0.4em] uppercase text-muted-foreground">
              Simple Pricing
            </span>
          </motion.div>
          
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-7xl font-light tracking-tight text-foreground mb-4"
          >
            Choose Your Plan
          </motion.h1>
          
          <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground font-light mb-8 max-w-2xl mx-auto leading-relaxed"
          >
            Simple, transparent pricing to secure your business from content theft and piracy.
          </motion.p>

          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center bg-muted/30 rounded-full p-1 border border-border/40"
          >
            <button
              className={`px-8 py-2.5 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase transition-all duration-300 ${!isAnnual
                  ? 'bg-foreground text-background shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
              onClick={() => setIsAnnual(false)}
            >
              Monthly
            </button>
            <button
              className={`px-8 py-2.5 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase transition-all duration-300 ${isAnnual
                  ? 'bg-foreground text-background shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
              onClick={() => setIsAnnual(true)}
            >
              Annual (Save 20%)
            </button>
          </motion.div>
        </div>

        {/* Setup & Onboarding Banner */}
        <SetupBanner onSelect={onSelectPlan} />

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-16 md:gap-10 max-w-7xl mx-auto pb-32 items-stretch">
          {plans.map((plan, index) => (
            <PricingCard 
              key={plan.name} 
              plan={plan} 
              index={index} 
              isAnnual={isAnnual} 
              calculatePrice={calculatePrice} 
              onSelect={onSelectPlan}
            />
          ))}
        </div>
      </div>

      <Footer />
    </motion.div>
  );
};

function SetupBanner({ onSelect }: { onSelect?: () => void }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative max-w-4xl mx-auto mb-20 rounded-[40px] p-[2px] overflow-hidden group cursor-pointer"
      onClick={onSelect}
    >
      <GlowingEffect
        spread={100}
        glow={isHovered}
        disabled={false}
        proximity={200}
        inactiveZone={0.01}
        borderWidth={1}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-blue-500/20 to-emerald-500/20 animate-gradient-x opacity-40 group-hover:opacity-60 transition-opacity" />
      <div className="relative bg-card/80 backdrop-blur-3xl rounded-[38px] p-8 md:p-12 border border-border/20 shadow-2xl overflow-hidden">
         <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none">
           <Construction size={240} strokeWidth={0.5} />
         </div>
         
         <div className="flex flex-col md:flex-row items-center justify-between gap-12 relative z-10">
           <div className="flex-1 space-y-6 text-center md:text-left">
             <div className="flex items-center justify-center md:justify-start gap-3">
               <div className="p-3 bg-foreground/5 rounded-2xl border border-foreground/10">
                 <Construction className="size-6 text-foreground" />
               </div>
               <h2 className="text-2xl font-light tracking-tight">One-Time Setup Fee</h2>
             </div>
             <p className="text-muted-foreground font-light leading-relaxed max-w-lg mx-auto md:mx-0">
               Every partnership starts with a full security setup. This covers your custom branding, server configuration, and a personal onboarding call.
             </p>
             <div className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-3">
               {["Custom Branding", "Portal Setup", "Onboarding Call", "Security Config"].map((t, idx) => (
                 <div key={idx} className="flex items-center gap-2">
                   <Check className="size-3 text-emerald-500" />
                   <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground/60">{t}</span>
                 </div>
               ))}
             </div>
           </div>
           <div className="flex flex-col items-center md:items-end gap-2 shrink-0">
              <span className="text-6xl font-light tracking-tighter text-foreground">$5,000</span>
              <span className="text-[10px] font-bold tracking-[0.4em] uppercase text-muted-foreground/40">Initial Investment</span>
           </div>
         </div>
      </div>
    </motion.div>
  );
}

function PricingCard({ plan, index, calculatePrice, onSelect }: any) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <motion.div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.5 + index * 0.1 }}
      className={`group relative rounded-[40px] border transition-all duration-700 flex flex-col ${plan.highlighted
          ? 'border-foreground/20 bg-muted/10 scale-[1.05] z-10 shadow-2xl'
          : 'border-border/40 bg-background/50 hover:border-border/60 hover:bg-muted/5'
        } p-10`}
    >
      <GlowingEffect
        spread={60}
        glow={plan.highlighted || isHovered}
        disabled={false}
        proximity={120}
        inactiveZone={0.01}
        borderWidth={2}
      />

      {plan.highlighted && (
        <div className="absolute -top-5 left-0 right-0 z-[20] flex justify-center pointer-events-none">
          <div className="relative px-6 py-2 bg-foreground text-background rounded-full shadow-[0_4px_25px_rgba(255,255,255,0.15)] border border-white/10 overflow-hidden">
             {/* Animating highlight for the badge */}
            <motion.div 
               animate={{ x: ['-100%', '100%'] }} 
               transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
               className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12" 
            />
            <div className="flex items-center gap-2.5 relative z-10">
              <div className="relative size-2">
                <span className="absolute inset-0 bg-background rounded-full animate-ping opacity-40" />
                <span className="relative block size-full rounded-full bg-background" />
              </div>
              <span className="text-[10px] font-bold tracking-[0.4em] uppercase whitespace-nowrap">Recommended Choice</span>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 mb-8 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-light tracking-tight text-foreground">{plan.name}</h3>
          {plan.icon}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-light tracking-tight text-muted-foreground/50">$</span>
          <span className="text-5xl font-light tracking-tighter text-foreground">
            {calculatePrice(plan.monthlyPrice)}
          </span>
          <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground/40">
            /month
          </span>
        </div>
        <p className="text-sm text-muted-foreground font-light leading-relaxed h-12">{plan.description}</p>
      </div>

      <div className="relative z-10 space-y-4 mb-12 flex-1 pt-6 border-t border-border/20">
        {plan.features.map((feature: string, i: number) => (
          <div key={i} className="flex items-start gap-4">
            <Check className="h-4 w-4 text-emerald-500 mt-1 shrink-0" strokeWidth={3} />
            <span className="text-[13px] text-muted-foreground/90 font-light leading-relaxed">{feature}</span>
          </div>
        ))}
        <div className="pt-6 mt-4 border-t border-border/10">
          <div className="flex items-center gap-2">
            <div className="size-1.5 rounded-full bg-blue-500/40" />
            <span className="text-[11px] font-bold tracking-widest uppercase text-foreground/80">
              {plan.limits}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-auto pt-6">
        <button
          onClick={onSelect}
          className={`relative z-10 w-full py-5 px-6 rounded-[24px] text-[11px] font-bold tracking-[0.4em] uppercase transition-all duration-500 flex items-center justify-center gap-3 group/btn ${plan.highlighted
              ? 'bg-foreground text-background hover:scale-[0.98] shadow-xl shadow-foreground/5'
              : 'border border-border text-foreground hover:bg-foreground hover:text-background hover:border-foreground'
            }`}
        >
          {plan.cta}
          <ArrowRight size={14} className="transition-transform group-hover/btn:translate-x-1" />
        </button>
      </div>
    </motion.div>
  );
}